// src/shared/components/PwaInstallControl.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n/I18nProvider';
import {
  detectInstallPlatform,
  isStandalonePwa,
  takeDeferredInstallPrompt,
} from '../utils/pwaInstallPromptBridge';
import './PwaInstallControl.css';

function PlatformIcon({ platform }) {
  if (platform === 'windows') {
    return (
      <svg className="npc-pwa-platform-icon npc-pwa-platform-icon--windows" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M2.4 4.2 10.7 3v8H2.4V4.2Zm9.5-1.4L21.6 1.4V11h-9.7V2.8ZM2.4 12.3h8.3v8L2.4 19.1v-6.8Zm9.5 0h9.7v9.5l-9.7-1.4v-8.1Z" />
      </svg>
    );
  }

  if (platform === 'android') {
    return (
      <svg className="npc-pwa-platform-icon npc-pwa-platform-icon--android" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7.2 7.7h9.6c.8 0 1.5.7 1.5 1.5v7.1c0 .8-.7 1.5-1.5 1.5H16v2.5c0 .6-.5 1.1-1.1 1.1s-1.1-.5-1.1-1.1v-2.5h-3.6v2.5c0 .6-.5 1.1-1.1 1.1S8 20.9 8 20.3v-2.5h-.8c-.8 0-1.5-.7-1.5-1.5V9.2c0-.8.7-1.5 1.5-1.5Zm-.1-1.3c.5-1.7 1.7-3 3.2-3.6L9.5 1.4a.6.6 0 0 1 .2-.8.6.6 0 0 1 .8.2l.9 1.6h1.2l.9-1.6a.6.6 0 0 1 .8-.2.6.6 0 0 1 .2.8l-.8 1.4c1.5.6 2.7 1.9 3.2 3.6H7.1Zm2.5-1.6a.7.7 0 1 0 0 1.4.7.7 0 0 0 0-1.4Zm4.8 0a.7.7 0 1 0 0 1.4.7.7 0 0 0 0-1.4ZM3.7 8.5c.6 0 1.1.5 1.1 1.1v5.5c0 .6-.5 1.1-1.1 1.1s-1.1-.5-1.1-1.1V9.6c0-.6.5-1.1 1.1-1.1Zm16.6 0c.6 0 1.1.5 1.1 1.1v5.5c0 .6-.5 1.1-1.1 1.1s-1.1-.5-1.1-1.1V9.6c0-.6.5-1.1 1.1-1.1Z" />
      </svg>
    );
  }

  return (
    <svg className="npc-pwa-platform-icon npc-pwa-platform-icon--apple" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16.9 12.9c0-2 1.6-3 1.7-3.1-.9-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.3 2-1.4 2.4-.4 6 1 8 .7 1 1.5 2 2.6 1.9 1-.1 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1 0 1.8-1 2.5-2 .8-1.2 1.2-2.4 1.2-2.5-.1 0-2.6-1-2.6-3.7ZM14.9 6.9c.5-.6.9-1.5.8-2.4-.8 0-1.8.5-2.4 1.1-.5.6-.9 1.4-.8 2.3.9.1 1.8-.4 2.4-1Z" />
    </svg>
  );
}

function IosInstallGuide({ open, onClose, t }) {
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const body = document.body;
    const html = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="npc-pwa-guide-overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        className="npc-pwa-guide-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="npcPwaInstallTitle"
      >
        <header className="npc-pwa-guide-header">
          <div className="npc-pwa-guide-heading">
            <div className="npc-pwa-safari-chip">
              <img src="/assets/pwa/safari.png" alt="" />
              <span>{t('pwa.ios.browserChip', null, 'iPhone · Safari')}</span>
            </div>

            <h2 id="npcPwaInstallTitle">{t('pwa.ios.title', null, 'Install Night Poker Club')}</h2>
            <p>{t('pwa.ios.description', null, 'Add Night Poker Club to your Home Screen and launch it like an app.')}</p>
          </div>

          <button
            className="npc-pwa-guide-close"
            type="button"
            onClick={onClose}
            aria-label={t('common.close', null, 'Close')}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </header>

        <div className="npc-pwa-guide-scroll">
          <div className="npc-pwa-safari-notice">
            <img src="/assets/pwa/safari.png" alt="Safari" />
            <div>
              <strong>{t('pwa.ios.openSafariTitle', null, 'Open this page in Safari')}</strong>
              <span>{t('pwa.ios.openSafariDesc', null, "The install option is available from Safari's Share menu.")}</span>
            </div>
          </div>

          <article className="npc-pwa-guide-card npc-pwa-guide-card--share">
            <div className="npc-pwa-guide-focus-stage">
              <img
                className="npc-pwa-guide-image"
                src="/assets/pwa/ios-step-1-share.jpg"
                alt="Safari menu showing Share"
              />
            </div>
            <div className="npc-pwa-guide-gradient" />
            <div className="npc-pwa-guide-number">01</div>
            <div className="npc-pwa-touch-target npc-pwa-touch-target--share" aria-hidden="true">
              <span>{t('pwa.ios.tapHere', null, 'Tap here')}</span>
            </div>
            <div className="npc-pwa-guide-copy">
              <span className="npc-pwa-guide-step-label">{t('pwa.ios.step1Label', null, 'STEP 1')}</span>
              <h3>{t('pwa.ios.step1Title', null, 'Tap “Share”')}</h3>
              <p>{t('pwa.ios.step1Desc', null, "Open Safari's menu and tap Share.")}</p>
            </div>
          </article>

          <article className="npc-pwa-guide-card npc-pwa-guide-card--home">
            <div className="npc-pwa-guide-focus-stage">
              <img
                className="npc-pwa-guide-image"
                src="/assets/pwa/ios-step-2-home-screen.jpg"
                alt="Safari Share menu showing Add to Home Screen"
              />
            </div>
            <div className="npc-pwa-guide-gradient" />
            <div className="npc-pwa-guide-number">02</div>
            <div className="npc-pwa-touch-target npc-pwa-touch-target--home" aria-hidden="true">
              <span>{t('pwa.ios.tapHere', null, 'Tap here')}</span>
            </div>
            <div className="npc-pwa-guide-copy">
              <span className="npc-pwa-guide-step-label">{t('pwa.ios.step2Label', null, 'STEP 2')}</span>
              <h3>{t('pwa.ios.step2Title', null, 'Select “Add to Home Screen”')}</h3>
              <p>{t('pwa.ios.step2Desc', null, 'From the Share menu, select Add to Home Screen.')}</p>
            </div>
          </article>

          <article className="npc-pwa-guide-card npc-pwa-guide-card--add">
            <div className="npc-pwa-guide-focus-stage">
              <img
                className="npc-pwa-guide-image"
                src="/assets/pwa/ios-step-3-add.jpg"
                alt="Add to Home Screen screen with Open as Web App enabled"
              />
            </div>
            <div className="npc-pwa-guide-gradient" />
            <div className="npc-pwa-guide-number">03</div>
            <div className="npc-pwa-touch-target npc-pwa-touch-target--add" aria-hidden="true">
              <span>{t('pwa.ios.tapAdd', null, 'Tap Add')}</span>
            </div>
            <div className="npc-pwa-guide-copy">
              <span className="npc-pwa-guide-step-label">{t('pwa.ios.step3Label', null, 'STEP 3')}</span>
              <h3>{t('pwa.ios.step3Title', null, 'Keep “Open as Web App” ON')}</h3>
              <p>{t('pwa.ios.step3Desc', null, 'Leave Open as Web App enabled, then tap Add.')}</p>
            </div>
          </article>

          <div className="npc-pwa-finish-card">
            <div className="npc-pwa-finish-check" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M5 12.5l4.2 4.2L19 7" />
              </svg>
            </div>
            <div>
              <strong>{t('pwa.ios.completeTitle', null, 'Installation complete')}</strong>
              <span>{t('pwa.ios.completeDesc', null, 'Launch Night Poker Club from the icon added to your Home Screen.')}</span>
            </div>
          </div>
        </div>

        <footer className="npc-pwa-guide-footer">
          <button type="button" className="npc-pwa-guide-done" onClick={onClose}>
            {t('pwa.ios.gotIt', null, 'GOT IT')}
          </button>
        </footer>
      </section>
    </div>,
    document.body
  );
}

export default function PwaInstallControl({ baseFontSize = 8, disabled = false }) {
  const { t } = useI18n();
  // Local/design preview helper:
  //   http://localhost:3000/?previewIOS=1
  // Forces only this install control to behave like iOS so the iOS guide popup
  // can be reviewed from a desktop browser. Normal URLs keep real detection.
  const forceIosPreview = useMemo(() => {
    if (typeof window === 'undefined') return false;

    try {
      return new URLSearchParams(window.location.search).get('previewIOS') === '1';
    } catch (_) {
      return false;
    }
  }, []);

  const platform = useMemo(
    () => (forceIosPreview ? 'ios' : detectInstallPlatform()),
    [forceIosPreview]
  );

  const [standalone, setStandalone] = useState(() => isStandalonePwa());
  const [guideOpen, setGuideOpen] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncStandalone = () => {
      const next = isStandalonePwa();
      setStandalone(next);
      if (next) setGuideOpen(false);
    };

    const queries = [
      '(display-mode: standalone)',
      '(display-mode: fullscreen)',
      '(display-mode: minimal-ui)',
      '(display-mode: window-controls-overlay)',
    ];

    const mediaQueries = queries
      .map((query) => {
        try {
          return window.matchMedia(query);
        } catch (_) {
          return null;
        }
      })
      .filter(Boolean);

    mediaQueries.forEach((media) => {
      try {
        media.addEventListener('change', syncStandalone);
      } catch (_) {
        media.addListener?.(syncStandalone);
      }
    });

    window.addEventListener('pageshow', syncStandalone);
    window.addEventListener('focus', syncStandalone);
    document.addEventListener('visibilitychange', syncStandalone);

    syncStandalone();

    return () => {
      mediaQueries.forEach((media) => {
        try {
          media.removeEventListener('change', syncStandalone);
        } catch (_) {
          media.removeListener?.(syncStandalone);
        }
      });

      window.removeEventListener('pageshow', syncStandalone);
      window.removeEventListener('focus', syncStandalone);
      document.removeEventListener('visibilitychange', syncStandalone);
    };
  }, []);

  const isIos = platform === 'ios';

  // Visibility rule:
  // - Any normal web-browser tab/window: always show Download.
  // - An actual installed PWA/standalone window: never show Download.
  //
  // Do NOT tie button visibility to beforeinstallprompt availability.
  // Chrome may withhold that event on localhost, when already installed,
  // or until its own installability checks are complete.
  if (standalone) {
    return null;
  }

  const handleDownload = async () => {
    if (disabled || installing) return;

    if (isIos) {
      setGuideOpen(true);
      return;
    }

    const promptEvent = takeDeferredInstallPrompt();
    if (!promptEvent) return;

    setInstalling(true);

    try {
      await promptEvent.prompt();

      // Waiting for userChoice is optional on some implementations.
      if (promptEvent.userChoice?.then) {
        await promptEvent.userChoice.catch(() => null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[PWA] install prompt failed:', error);
    } finally {
      setInstalling(false);
      setStandalone(isStandalonePwa());
    }
  };

  return (
    <>
      <div className="npc-pwa-login-download-wrap">
        <button
          type="button"
          className="npc-pwa-login-download"
          disabled={disabled || installing}
          onClick={handleDownload}
          style={{ fontSize: baseFontSize }}
        >
          <PlatformIcon platform={platform} />
          <span>{t('pwa.download', null, 'Download')}</span>
        </button>
      </div>

      {isIos ? (
        <IosInstallGuide
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
          t={t}
        />
      ) : null}
    </>
  );
}
